import { NetworkError } from 'common/errors'
import React, { useCallback, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { BaseError } from 'src/editor/api/errors/base-error'

export interface UseApiWrapperWithValidationProps<
  Response,
  RequestArgs extends unknown[],
> {
  requestFunction: (...requestArgs: RequestArgs) => Promise<Response>
  handleSuccess?: (response: Response, requestArgs: RequestArgs) => void
  handleError?: (error: Error) => void
  handleFinally?: () => void
  successToastMessage: string
}

export const useApiWrapperWithValidation = <
  Response,
  RequestArgs extends unknown[],
>({
  requestFunction,
  handleSuccess,
  handleError,
  handleFinally,
  successToastMessage,
}: UseApiWrapperWithValidationProps<Response, RequestArgs>) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)

  const fetcher = useCallback(
    async (...requestArgs: RequestArgs) => {
      try {
        setIsLoading(true)
        const response = await requestFunction(...requestArgs)
        handleSuccess?.(response, requestArgs)
        toast.success(successToastMessage)
      } catch (error) {
        if (error instanceof NetworkError) {
          toast.error(t('core.errors.no_connection'))
        } else if (error instanceof BaseError) {
          toast.error(() => (
            <div
              className="[&>a]:text-textLink [&>a]:hover:text-textLinkHover [&>a]:focus:text-textLinkPressed"
              dangerouslySetInnerHTML={{
                __html: error?.response?.detail,
              }}
            />
          ))
        } else {
          // biome-ignore lint/suspicious/noConsole: <explanation>
          console.log(`--error--`, error)
        }
        handleError?.(error as Error)
      } finally {
        setIsLoading(false)
        handleFinally?.()
      }
    },
    [
      t,
      handleSuccess,
      handleError,
      successToastMessage,
      requestFunction,
      handleFinally,
    ],
  )

  return {
    isLoading,
    fetcher,
  }
}
